import { useState } from 'react';

/**
 * Estabilidad visual del partial vivo del STT ("tinta que se seca").
 *
 * Cada partial de Scribe es una RE-HIPÓTESIS completa de la utterance: con
 * poco audio las últimas palabras (y a veces las primeras) son provisorias y
 * "bailan" hasta que llega más contexto. Mostrarlas igual que el texto firme
 * hace que la revisión se lea como "la app se equivoca y se corrige".
 *
 * La regla: una palabra se considera ESTABLE recién cuando sobrevivió
 * idéntica a dos emisiones consecutivas (la actual y la anterior). El prefijo
 * estable se pinta sólido; la cola nueva/revisada se pinta atenuada
 * (`.live-draft`), de modo que lo que baila siempre está marcado como
 * borrador. El primer partial de una utterance queda TODO como borrador.
 *
 * Comparación por palabra, insensible a mayúsculas y puntuación en los
 * bordes: Scribe suele revisar sólo el casing o la puntuación final de una
 * palabra ya oída — eso no debe degradarla a borrador.
 */

/**
 * Chars del prefijo de `next` cuyas palabras coinciden (normalizadas) con el
 * prefijo de `prev`. Devuelve el offset de fin de la última palabra estable.
 */
export function stableCharCount(prev: string, next: string): number {
  const norm = (w: string) =>
    w.toLowerCase().replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '');
  const prevWords = prev.split(/\s+/).filter(Boolean).map(norm);
  let stableEnd = 0;
  let i = 0;
  const re = /\S+/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(next)) !== null) {
    if (i >= prevWords.length) break;
    if (norm(m[0]) !== prevWords[i]) break;
    stableEnd = m.index + m[0].length;
    i += 1;
  }
  return stableEnd;
}

/**
 * Offset (en chars de `transcript`) donde termina el prefijo estable del
 * partial vivo. Pasar `''` resetea (utterance nueva arranca toda borrador).
 * Patrón "derive from previous render" oficial de React: el ajuste sucede
 * durante el render, sin efectos.
 */
export function useLiveTranscriptDraft(transcript: string): number {
  const [prev, setPrev] = useState('');
  const [stableUpto, setStableUpto] = useState(0);
  if (transcript !== prev) {
    setPrev(transcript);
    setStableUpto(transcript.trim() === '' ? 0 : stableCharCount(prev, transcript));
  }
  return stableUpto;
}
